import { AnimatePresence, motion } from "motion/react";
import { Fragment, useCallback } from "react";
import { type Control, useController, useFieldArray } from "react-hook-form";
import type { BidPostData } from "validations";

import CheckBox from "@krea/shared/design-system/fields/Checkbox";
import { ControlledWrapper } from "@krea/shared/design-system/input/controlled-wrapper";
import { IntegerInput } from "@krea/shared/design-system/input/integer-input";
import { PromotionTypeEnum } from "@krea/shared/types/common";

import DetailsItem from "../../details/item";

const LABEL_MAP: Record<PromotionTypeEnum, string> = {
  [PromotionTypeEnum.INTEREST_FREE]: "Interest free months",
  [PromotionTypeEnum.AMORTISATION_FREE]: "Amortisation free months",
  [PromotionTypeEnum.EU_LOAN]: "Loan with EU support",
};

// EU_LOAN is a flag, not a duration: it carries no month count, so checking it
// must not demand a value (bidPromotionSchema requires one only for the others).
const TAKES_MONTH_COUNT: Record<PromotionTypeEnum, boolean> = {
  [PromotionTypeEnum.INTEREST_FREE]: true,
  [PromotionTypeEnum.AMORTISATION_FREE]: true,
  [PromotionTypeEnum.EU_LOAN]: false,
};

interface IPromotionsSelectorProps {
  control: Control<BidPostData>;
  supportedPromotionTypes: PromotionTypeEnum[];
}

export const PromotionsSelector = ({
  supportedPromotionTypes,
  control,
}: IPromotionsSelectorProps) => {
  const {
    formState: { isSubmitting },
  } = useController({
    name: "promotions",
    control,
  });
  const { fields, append, remove } = useFieldArray({
    control,
    name: "promotions",
  });

  const getPromotionIndex = useCallback(
    (supportedPromotionType: PromotionTypeEnum) => {
      return fields.findIndex((item) => item.type === supportedPromotionType);
    },
    [fields],
  );

  return (
    <div>
      <ul className="list-none">
        {supportedPromotionTypes.map((supportedPromotionType) => {
          const typeIndex = getPromotionIndex(supportedPromotionType);
          const field = typeIndex > -1 ? fields[typeIndex] : null;

          return (
            <Fragment
              key={field ? field.id : `promotions-${supportedPromotionType}`}
            >
              <li className="mb-2">
                <CheckBox
                  id={`checkbox-${supportedPromotionType}`}
                  label={LABEL_MAP[supportedPromotionType]}
                  checked={!!field}
                  onChange={(checked) => {
                    if (checked) {
                      append({ type: supportedPromotionType });
                    } else if (field) {
                      remove(typeIndex);
                    }
                  }}
                  disabled={isSubmitting}
                />
              </li>

              <AnimatePresence>
                {field && TAKES_MONTH_COUNT[supportedPromotionType] && (
                  <motion.div
                    initial={{
                      opacity: 0,
                      height: 0,
                    }}
                    animate={{
                      opacity: 1,
                      height: "auto",
                      transition: {
                        type: "tween",
                        duration: 0.1,
                        ease: "easeOut",
                      },
                    }}
                    exit={{
                      opacity: 0,
                      height: 0,
                      transition: {
                        type: "tween",
                        duration: 0.1,
                        ease: "easeOut",
                      },
                    }}
                  >
                    <DetailsItem className="mb-4 rounded-sm border border-gray-300 p-4">
                      <div className="w-full">
                        <ControlledWrapper
                          label="Number of months the promotion refers to"
                          name={`promotions.${typeIndex}.value`}
                          control={control}
                          required={true}
                          render={({ field, fieldState }) => (
                            <IntegerInput
                              {...field}
                              id={`promotions.${typeIndex}.value`}
                              disabled={isSubmitting}
                              data-invalid={fieldState.invalid}
                            />
                          )}
                        />
                      </div>
                    </DetailsItem>
                  </motion.div>
                )}
              </AnimatePresence>
            </Fragment>
          );
        })}
      </ul>
    </div>
  );
};
